import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { formatDate } from "../lib/format.js";
import { PageSpinner, EmptyState, Modal } from "../components/ui.js";
import { IconClipboard } from "../components/icons.js";

interface JournalEntry { id: string; entryDate: string; mood: number | null; title: string | null; body: string; createdAt: string }

const MOODS = ["😔", "😕", "😐", "🙂", "😄"];

function localDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function JournalPage() {
  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [composing, setComposing] = useState(false);

  const load = useCallback(async () => {
    try { setEntries((await api.get<{ entries: JournalEntry[] }>("/api/journal")).entries); }
    catch { setEntries([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h1>Journal</h1>
        <p className="subtitle">The record only you keep. Private by default — never sent to AI unless you allow it.</p>
      </div>
      <button className="btn btn-primary" style={{ marginBottom: "var(--space-5)" }} onClick={() => setComposing(true)}>New entry</button>

      {!entries ? <PageSpinner /> : entries.length === 0 ? (
        <EmptyState icon={<IconClipboard />} title="Nothing written yet">How did today actually go? What got in the way, what worked? A line is enough.</EmptyState>
      ) : (
        <div className="stack">
          {entries.map((e) => (
            <div className="card" key={e.id}>
              <div className="row-between" style={{ alignItems: "flex-start", gap: "var(--space-3)" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {e.mood != null && <span style={{ fontSize: "1.2rem" }}>{MOODS[e.mood - 1]}</span>}
                    {e.title && <span style={{ fontWeight: 600 }}>{e.title}</span>}
                  </div>
                  <p className="dim text-xs" style={{ margin: "2px 0 8px" }}>{formatDate(e.entryDate)}</p>
                  <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{e.body}</p>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => deleteEntry(e.id, load)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {composing && <ComposeModal onClose={() => setComposing(false)} onSaved={() => { setComposing(false); void load(); }} />}
    </div>
  );
}

async function deleteEntry(id: string, reload: () => Promise<void>) {
  if (!window.confirm("Delete this journal entry? This can't be undone.")) return;
  try { await api.del(`/api/journal/${id}`); await reload(); } catch { /* ignore */ }
}

function ComposeModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [mood, setMood] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!body.trim()) { setError("Write something first."); return; }
    setSaving(true); setError(null);
    try {
      await api.post("/api/journal", { date: localDate(), mood, title: title.trim() || null, body: body.trim() });
      onSaved();
    } catch { setError("Couldn't save. Try again."); setSaving(false); }
  }

  return (
    <Modal title="New journal entry" onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="field">
        <label>How was today?</label>
        <div style={{ display: "flex", gap: 8 }}>
          {MOODS.map((emoji, i) => (
            <button key={i} type="button" className={`mood-btn${mood === i + 1 ? " active" : ""}`} onClick={() => setMood(mood === i + 1 ? null : i + 1)}>{emoji}</button>
          ))}
        </div>
      </div>
      <div className="field">
        <label htmlFor="j-title">Title <span className="dim">(optional)</span></label>
        <input id="j-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="A word or two" />
      </div>
      <div className="field">
        <label htmlFor="j-body">Entry</label>
        <textarea id="j-body" className="textarea" rows={6} value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} placeholder="What happened, how you felt, what you noticed…" autoFocus />
      </div>
      <div className="row mt-4">
        <button className="btn btn-primary grow" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save entry"}</button>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </Modal>
  );
}
